import { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { TanTable } from '../../../components/TanTable';
import Notification, { NotificationContainer } from '../../../components/Notification';
import remindersService from '../../../services/compliance/reminders.service';
import {
  AlertType,
  ReminderLog,
  ReminderLogFilters,
  ReminderStatus,
} from '../../../types/compliance';

const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  contract_renewal: 'Contract Renewal',
  contract_renewal_action: 'Contract Renewal Action',
  contract_notice_period: 'Contract Notice Period',
  tprm_review: 'TPRM Review',
  remediation_due: 'Remediation Due',
  risk_acceptance_expiry: 'Risk Acceptance Expiry',
  evidence_expiry: 'Evidence Expiry',
};

const ALERT_TYPE_BADGE: Record<AlertType, string> = {
  contract_renewal: 'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  contract_renewal_action: 'bg-indigo-200 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  contract_notice_period: 'bg-purple-200 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  tprm_review: 'bg-teal-200 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
  remediation_due: 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  risk_acceptance_expiry: 'bg-orange-200 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  evidence_expiry: 'bg-pink-200 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
};

const STATUS_BADGE: Record<ReminderStatus, string> = {
  sent: 'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200',
  failed: 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
  pending: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  skipped: 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function truncate(s: string | null | undefined, n = 60): string {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n)}...` : s;
}

export default function ReminderLogTab() {
  const [data, setData] = useState<ReminderLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<ReminderLogFilters>({});
  const [recordSearch, setRecordSearch] = useState('');
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [errorNotification, setErrorNotification] =
    useState<{ title: string; message: string } | null>(null);
  const [successNotification, setSuccessNotification] =
    useState<{ title: string; message: string } | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await remindersService.listLogs(filters);
      setData(rows);
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to load logs', message: msg });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // "Record" box: case-insensitive match on the name shown in the table.
  const visibleData = useMemo(() => {
    const needle = recordSearch.trim().toLowerCase();
    if (!needle) return data;
    return data.filter((r) => (r.entity_name ?? '').toLowerCase().includes(needle));
  }, [data, recordSearch]);

  const handleResend = useCallback(
    async (row: ReminderLog) => {
      setResendingId(row.id);
      try {
        const res = await remindersService.resend(row.id);
        setSuccessNotification({
          title: 'Resend Complete',
          message: `Log ${row.id.slice(0, 8)} → ${res.status}${res.failure_reason ? ` (${res.failure_reason})` : ''}`,
        });
        loadData();
      } catch (error: any) {
        const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
        setErrorNotification({ title: 'Resend Failed', message: msg });
      } finally {
        setResendingId(null);
      }
    },
    [loadData],
  );

  const setFilter = <K extends keyof ReminderLogFilters>(key: K, value: ReminderLogFilters[K]) => {
    setFilters((f) => ({ ...f, [key]: value || undefined }));
  };

  const columns = useMemo<ColumnDef<ReminderLog>[]>(
    () => [
      {
        accessorKey: 'alert_type',
        header: () => <span className="pl-3">Alert Type</span>,
        enableSorting: true,
        size: 200,
        cell: ({ row }) => {
          const t = row.original.alert_type;
          return (
            <span className="pl-3">
              <span
                className={`px-2 py-0.5 text-xs font-medium rounded ${ALERT_TYPE_BADGE[t] || ''}`}
              >
                {ALERT_TYPE_LABELS[t] || t}
              </span>
            </span>
          );
        },
      },
      {
        accessorKey: 'label',
        header: 'Label',
        enableSorting: true,
        size: 200,
        cell: ({ row }) => <span>{row.original.label}</span>,
      },
      {
        // What the reminder was about, by name. The id stays 'contract_id' so
        // saved table layouts keep working; the value is the name so sorting
        // and column filters show names too.
        id: 'contract_id',
        accessorFn: (r) => r.entity_name ?? '',
        header: 'Record',
        enableSorting: true,
        size: 200,
        cell: ({ row }) => {
          const r = row.original;
          if (!r.entity_name) {
            return r.entity_id || r.contract_id
              ? <span className="text-muted">(record removed)</span>
              : <span>—</span>;
          }
          const to = r.contract_id
            ? `/compliance/contracts?tab=data&id=${r.contract_id}`
            : r.vendor_id
              ? `/compliance/tprm/vendors/${r.vendor_id}`
              : null;
          if (!to) return <span>{r.entity_name}</span>;
          return (
            <Link
              to={to}
              className="text-blue-600 dark:text-blue-400 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {r.entity_name}
            </Link>
          );
        },
      },
      {
        accessorKey: 'scheduled_send_date',
        header: 'Scheduled',
        enableSorting: true,
        size: 120,
        cell: ({ row }) => <span>{formatDate(row.original.scheduled_send_date)}</span>,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: true,
        size: 100,
        cell: ({ row }) => {
          const s = row.original.status;
          return (
            <span className={`px-2 py-0.5 text-xs font-medium rounded ${STATUS_BADGE[s] || ''}`}>
              {s}
            </span>
          );
        },
      },
      {
        accessorKey: 'sent_at',
        header: 'Sent At',
        enableSorting: true,
        size: 160,
        cell: ({ row }) => <span>{formatDateTime(row.original.sent_at)}</span>,
      },
      {
        accessorKey: 'retry_count',
        header: 'Retries',
        enableSorting: true,
        size: 80,
        cell: ({ row }) => <span>{row.original.retry_count}</span>,
      },
      {
        accessorKey: 'failure_reason',
        header: 'Failure Reason',
        enableSorting: false,
        size: 240,
        cell: ({ row }) => {
          const r = row.original.failure_reason;
          if (!r) return <span>—</span>;
          return (
            <span title={r} className="truncate block max-w-xs">
              {truncate(r, 60)}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          if (row.original.status !== 'failed') return null;
          const isResending = resendingId === row.original.id;
          return (
            <div className="flex gap-2 justify-end">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleResend(row.original);
                }}
                disabled={isResending}
                className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors disabled:opacity-50"
                title="Resend"
              >
                {isResending ? (
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <ArrowPathIcon className="w-4 h-4" />
                )}
              </button>
            </div>
          );
        },
      },
    ],
    [handleResend, resendingId],
  );

  const selectCls =
    'px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white';

  return (
    <>
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Alert Type</label>
          <select
            value={filters.alert_type || ''}
            onChange={(e) => setFilter('alert_type', (e.target.value as AlertType) || undefined)}
            className={selectCls}
          >
            <option value="">All</option>
            {(Object.keys(ALERT_TYPE_LABELS) as AlertType[]).map((t) => (
              <option key={t} value={t}>
                {ALERT_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select
            value={filters.status || ''}
            onChange={(e) => setFilter('status', (e.target.value as ReminderStatus) || undefined)}
            className={selectCls}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="skipped">Skipped</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Date From</label>
          <input
            type="date"
            value={filters.date_from || ''}
            onChange={(e) => setFilter('date_from', e.target.value || undefined)}
            className={selectCls}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Date To</label>
          <input
            type="date"
            value={filters.date_to || ''}
            onChange={(e) => setFilter('date_to', e.target.value || undefined)}
            className={selectCls}
          />
        </div>
        <div>
          <label htmlFor="reminder-log-record" className="block text-xs text-gray-500 mb-1">
            Record
          </label>
          <input
            id="reminder-log-record"
            type="text"
            value={recordSearch}
            onChange={(e) => setRecordSearch(e.target.value)}
            placeholder="Contract or vendor name..."
            className={selectCls}
          />
        </div>
      </div>

      <TanTable
        data={visibleData}
        columns={columns}
        loading={loading}
        settingsKey="compliance-reminder-logs-table"
        fieldName="compliance-reminder-logs-table"
        searchPlaceholder="Search logs..."
        enableGrouping={false}
        enableColumnFilters={true}
        cardTitle="Reminder Log"
        lockedColumns={[{ id: 'actions', side: 'right' }]}
      />

      {errorNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
            autoDismiss={5000}
          />
        </NotificationContainer>
      )}

      {successNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="success"
            title={successNotification.title}
            message={successNotification.message}
            showClose={true}
            onClose={() => setSuccessNotification(null)}
            autoDismiss={5000}
          />
        </NotificationContainer>
      )}
    </>
  );
}
